import { useEffect, useRef, useState } from 'react';
import { distanceKm } from '../../../supabase/functions/_shared/placeSearch.ts';
import { searchPlaces, shortAddress, type FoundPlace } from '../../lib/placePicker';
import { Spinner } from '../design-system';

interface PlaceFinderProps {
  /** The host's area: results are ordered nearest first and show a distance. */
  near: { lat: number; lng: number } | null;
  onPick: (place: FoundPlace) => void;
  /** "Add it by hand": the person types the address instead. */
  onManual: () => void;
  autoFocus?: boolean;
}

function distanceLabel(near: { lat: number; lng: number } | null, p: FoundPlace): string | null {
  if (!near) return null;
  const km = distanceKm(near, p);
  if (km < 1) return `${Math.max(1, Math.round(km * 10) * 100)} m`;
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

/**
 * One search box for a place's name or address. Waits for a pause in typing,
 * lists what it finds with the street, the kind of place and the distance, and
 * hands the chosen one back. Arrow keys and Enter work.
 */
export function PlaceFinder({ near, onPick, onManual, autoFocus = true }: PlaceFinderProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoundPlace[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0); // only the newest search may update the list

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function change(value: string) {
    setQuery(value);
    setFailed(false);
    if (timer.current) clearTimeout(timer.current);
    const q = value.trim();
    if (q.length < 3) {
      seq.current++;
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const mine = ++seq.current;
    timer.current = setTimeout(async () => {
      try {
        const found = await searchPlaces(q, near);
        if (mine !== seq.current) return;
        setResults(found);
        setActive(0);
      } catch {
        if (mine !== seq.current) return;
        setResults([]);
        setFailed(true);
      }
      if (mine === seq.current) setLoading(false);
    }, 350);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); onPick(results[active]); }
  }

  const searched = query.trim().length >= 3 && !loading;

  return (
    <div className="flex flex-col gap-3">
      <label htmlFor="place-search" className="text-sm font-semibold text-app-fg-2">Find the place</label>
      <div className="flex items-center gap-3 h-14 px-4 rounded-2xl bg-app-bg border border-gold focus-within:ring-1 focus-within:ring-gold">
        <i className="ti ti-search text-gold text-lg" aria-hidden="true" />
        <input
          id="place-search"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls="place-results"
          aria-autocomplete="list"
          autoFocus={autoFocus}
          autoComplete="off"
          value={query}
          onChange={(e) => change(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Name or address, e.g. Taberna Belga"
          className="flex-1 min-w-0 bg-transparent outline-none text-base text-app-fg placeholder:text-app-muted"
        />
        {loading && <Spinner size="sm" />}
      </div>
      {near && (
        <p className="text-xs text-app-muted flex items-center gap-1.5">
          <i className="ti ti-map-pin" aria-hidden="true" /> Nearest to your area first
        </p>
      )}

      {results.length > 0 && (
        <ul id="place-results" role="listbox" className="rounded-2xl border border-app-border bg-app-card overflow-hidden">
          {results.map((p, i) => {
            const dist = distanceLabel(near, p);
            return (
              <li key={`${p.id}-${i}`} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => onPick(p)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 min-h-[56px] border-b border-app-border last:border-0 transition-colors ${i === active ? 'bg-black/5' : ''}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-semibold text-app-fg truncate">{p.name}</span>
                    <span className="block text-[13px] text-app-muted truncate">{shortAddress(p) || 'Address not listed'}</span>
                  </span>
                  <span className="flex flex-col items-end gap-1 shrink-0">
                    {p.kind && <span className="text-[11px] font-medium text-gold bg-gold/15 rounded-full px-2 py-0.5">{p.kind}</span>}
                    {dist && <span className="text-[11px] text-app-muted">{dist}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {searched && results.length === 0 && (
        <p role="status" className="text-sm text-app-muted">
          {failed
            ? "Search isn't available right now. You can add the address by hand."
            : `Nothing found for "${query.trim()}". Try another spelling, or add it by hand.`}
        </p>
      )}

      <p className="text-sm text-app-muted">
        Not here? <button type="button" onClick={onManual} className="text-gold underline underline-offset-2">Add it by hand</button>
        {' '}with just an address.
      </p>
      <p className="text-[11px] text-app-muted/80">Map data © OpenStreetMap contributors</p>
    </div>
  );
}

export default PlaceFinder;
